/* eslint-disable react/display-name */
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Cropper } from 'react-cropper';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import {
  Button,
  DataGrid,
  Drawer,
  PopupMenu,
  TextInputWithLabel,
  Loader,
  FileUploader
} from 'quantux-ui';
import { addAppAlert } from '../../actions';
import { blue, gray, salmon } from '../../theme';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import { BASE_URL, titleCase, generateDisplayName, DRAWER_BREAK_POINT, USA_STATES } from '../../utils';

let controller = new AbortController();
const DOCS_PER_PAGE = 10;
const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};
const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const headerDefs = [
  // Practice Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Practice Name',
    field: 'practiceName',
    key: 'practiceName',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => , containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 281, minWidth: 281, maxWidth: 281, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const data = text.split('/');
      return (
        <React.Fragment>
          <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
            {data[0]}
          </div>
          <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
            {`NPI: ${data[1]}`}
          </div>
        </React.Fragment>
      );
    }
  },
  // Mailing address ()
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Primary Practice Address',
    field: 'mailingAddress',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      const addTmp = text.split(' ');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
      // Generate Google map url
      // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div className="fx fx__v-center">
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
            style={{ fontSize: '16px' }}
          >
            <i className="fa fa-map-marker" />
          </a>
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
          >
            <div>
              {addTmp.join(' ') + ' ' + zip}
            </div>
          </a>
        </div>
      );
    }
  },
  // Phone
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Phone',
    field: 'phone',
    key: 'phone',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => (
      <div>
        <a
          href={`tel:+1${text}`}
        >
          {`${text.substr(0, 3)}-${text.substr(3, 3)}-${text.substr(6, 4)}`}
        </a>
      </div>
    )
  },
  // Owners
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Practice Owners',
    field: 'practiceOwners',
    key: 'practiceOwners',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const names = text.split('_');
      const comps = [];
      names.forEach((el, idx) => {
        comps.push(
          <div key={`${idx}_${Date.now()}`}>
            {el}
          </div>
        )
      });
      return (
        <React.Fragment>
          {comps}
        </React.Fragment>
      );
    }
  }
];

const PracticesDataGrid = (props) => {
  const isFirstRender = useRef(true);
  const [showLoader, setShowLoader] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // Searching practices
  const [showSearchPracticeDrawer, setShowSearchPracticeDrawer] = useState(false);
  // For add user/provider drawers and copper
  const [isBannerCropperDrawerOpen, toggleBannerCropperDrawer] = useState(false);
  const [isAvatarCropperDrawerOpen, toggleAvatarCropperDrawer] = useState(false);
  const [isAddPracticeDrawerVisible, toggleAddPracticeDrawer] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [isDisplayNameValid, checkDisplayNameValidity] = useState(false);
  const [isDisplayNamePristine, setDisplayNamePristineState] = useState(true);
  const [userName, setUserName] = useState('');
  const [isUserNameValid, checkUserNameValidity] = useState(false);
  const [isUserNamePristine, setUserNamePristineState] = useState(true);
  const [isUserNameDuplicate, setUserNameDuplicateStatus] = useState(true);
  // --- Mailing address
  const [mailingStreetAddress, setMailingStreetAddress] = useState('');
  const [mailingSuite, setMailingSuite] = useState('');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingState, setMailingState] = useState('');
  const [mailingZip, setMailingZip] = useState('');
  // --- Primary address
  const [primaryStreetAddress, setPrimaryStreetAddress] = useState('');
  const [primarySuite, setPrimarySuite] = useState('');
  const [primaryCity, setPrimaryCity] = useState('');
  const [primaryState, setPrimaryState] = useState('');
  const [primaryZip, setPrimaryZip] = useState('');
  // ---
  const [phone, setPhone] = useState('');
  const [isPhoneValid, checkPhoneValidity] = useState(false);
  const [isPhonePristine, setPhonePristineState] = useState(true);
  const [fax, setFax] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [bannerImageUrl, setBannerImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
  const [avatarImageUrl, setAvatarImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailValid, checkEmailValidity] = useState(false);
  const [isEmailPristine, setEmailPristineState] = useState(true);
  const [isEmailDuplicate, setEmailDuplicateStatus] = useState(true);
  const [npi, setNpi] = useState('');
  const [website, setWebsite] = useState('');
  const [canAddPractice, setCanAddPractice] = useState(false);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);

  const [practiceName, setPracticeName] = useState('');
  const [practiceTaxonomy, setPracticeTaxonomy] = useState('');
  // Practice Search
  const [searchOrgName, setSearchOrgName] = useState('');
  const [searchTaxonomy, setSearchTaxonomy] = useState('');
  const [searchNpi, setSearchNpi] = useState('');
  const [npiErrMsg, setNpiErrMsg] = useState('');
  const [showNpiErrMessage, setShowNpiErrMessage] = useState(false);
  const [canSearchPractices, setCanSearchPractices] = useState(false);
  const [searchCity, setSearchCity] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [showStateMenu, setShowStateMenu] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [searchingPractices, setSearchingPractices] = useState(false);
  const [practiceSearchResults, setPracticeSearchResults] = useState([]);
  const [showZeroResultMessage, setShowZeroResultMessage] = useState(false);

  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 897);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 134);
  const [isMobileLayout, setMobileLayout] = useState(false);

  // Styles
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      // maxWidth: '804px',
      width: `${gridWidth}px`,
      overflow: 'hidden'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right',
      height: '40px',
      border: '1px solid black',
      paddingLeft: '4px',
      background: gray[500]
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    addUserMenu: {
      width: '184px',
      position: 'relative',
      marginBottom: '8px'
    },
    usersSearchMenu: {
      width: '350px',
      background: gray[700],
      overflow: 'hidden'
    },
    userSearchMenuHeading: {
      padding: '8px 0',
      textAlign: 'center'
    },
    userSearchMenuSearchingMsg: {
      color: blue[500],
      fontSize: '1.25rem',
      textAlign: 'center',
      padding: '24px'
    },
    userSearchMenuItems: {
      maxHeight: '250px',
      overflowY: 'auto'
    },
    userSearchMenuResultsMsg: {
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '120px'
    },
    drawerContainer: {
      // background: gray[500],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    practiceNameHeader: {
      color: blue[400]
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    imageUploaderContainer: {
      position: 'relative',
      maxHeight: '454px'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    userSearchItems: {
      cursor: 'pointer',
      ':hover': {
        background: gray[800]
      }
    },
    userSearchItemsIcon: {
      width: '40px',
      height: '40px',
      background: blue[500],
      color: 'white',
      fontSize: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '5px'
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    },
    npiErrMessage: {
      color: salmon[500],
      fontSize: '14px',
    },
    npiErrMessageContainer: {
      textAlign: 'right',
      paddingRight: '8px',
      paddingTop: '4px'
    }
  });

  const history = useHistory();

  // Refs
  const drawerBannerRef = React.createRef();
  const drawerAvatarRef = React.createRef();
  const bannerCropperRef = React.createRef();
  const bannerFileInputRef = React.createRef();
  const avatarCropperRef = React.createRef();
  const avatarFileInputRef = React.createRef();
  const gridEllipsisMenu = React.createRef();
  const gridParentRef = useRef();
  const stateRef = useRef();

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      // console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.9 * gridParentRefWidth);
      // console.log(`GridWidth = ${tmp}`);
      setGridWidth(Math.min(tmp, 897));
      setGridHeight(window.innerHeight - 134);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  // Get Data
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Check for duplicate username
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0) {
      const url = new URL(`${BASE_URL}/practice/isPracticeUserNameExists`);
      const params = { userName };
      url.search = new URLSearchParams(params).toString();
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          setUserNameDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [userName]);

  // Check for duplicate email
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isEmailValid) {
      const url = new URL(`${BASE_URL}/practice/isPracticeEmailExists`);
      const params = { email };
      url.search = new URLSearchParams(params).toString();
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          setEmailDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [email]);

  // Can add practice flag check
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const value =
      displayName.length >= 2 && displayName.length <= 40 &&
      userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0 &&
      !isUserNameDuplicate &&
      // streetAddress.length > 5 &&
      // city.length >= 2 &&
      // state.length === 2 &&
      // zip.length >= 5 &&
      isEmailValid &&
      !isEmailDuplicate;
    // isPhoneValid
    // practiceMembers.length > 0 && (practiceMembers.findIndex(el => el.accessType === 'Practice Owner') > -1);
    // practiceMembers.length > 0;
    setCanAddPractice(value);
  }, [
    displayName,
    userName,
    isUserNameDuplicate,
    mailingStreetAddress,
    mailingSuite,
    mailingCity,
    mailingState,
    mailingZip,
    email,
    isEmailValid,
    isEmailDuplicate,
    phone,
    isPhoneValid
    // practiceMembers
  ]);

  useEffect(() => {
    if (searchNpi.length === 0) {
      setShowNpiErrMessage(false);
      setNpiErrMsg('');
    } else if (isNaN(parseInt(searchNpi))) {
      setNpiErrMsg('NPI should only have digits');
      setShowNpiErrMessage(true);
    } else {
      if (searchNpi.length < 10 || searchNpi.length > 10 || searchNpi.indexOf(' ') > -1) {
        setShowNpiErrMessage(true);
        setNpiErrMsg('Atleast 10 digits');
      } else {
        setShowNpiErrMessage(false);
        setNpiErrMsg('');
      }
    }
  }, [searchNpi]);

  useEffect(() => {
    if (searchNpi.length === 0 && searchOrgName.length === 0) {
      setCanSearchPractices(false);
    } else {
      if (searchOrgName.length > 0 && searchNpi.length === 0) {
        setCanSearchPractices(true);
      } else if (searchOrgName.length === 0 && searchNpi.length > 0) {
        setCanSearchPractices(!showNpiErrMessage);
      } else {
        setCanSearchPractices(!showNpiErrMessage);
      }
    }
  }, [showNpiErrMessage, searchOrgName, searchNpi]);

  const getData = () => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    formData.append('filters', JSON.stringify(filters));
    formData.append('sorting', JSON.stringify(sorting));

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/allPractices`, requestOptions)
      .then(response => response.text())
      .then((result) => {
        const data = [];
        const reqData = JSON.parse(result);
        // console.log('reqData.users', reqData.users);
        for (let i = 0; i < reqData.practices.length; i++) {
          const activeRecord = reqData.practices[i];
          const add1 = activeRecord.mailingAddress.streetAddress1;
          const suite = ''; // activeRecord.mailingAddress.suite;
          const city = activeRecord.mailingAddress.city;
          const state = activeRecord.mailingAddress.state;
          const zip = activeRecord.mailingAddress.zip;
          let practiceOwners = '';
          // console.log(activeRecord.users);
          activeRecord.users.forEach((el, idx) => {
            if (el.accessType === 'Practice Owner') {
              practiceOwners += el.fullName;
            }
            if (idx !== activeRecord.users.length - 1) {
              practiceOwners += '_';
            }
          });

          data.push({
            id: activeRecord._id,
            practiceName: `${activeRecord.practiceName}/${activeRecord.npi}/${activeRecord._id}`,
            mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
            phone: activeRecord.phone.replaceAll('-', ''),
            npi: `${activeRecord.npi}`,
            practiceOwners
          });
        }
        setGridData(data);
        setShowLoader(false);
      })
      .catch(error => console.log('error', error));
  };

  const getUniquePracticeUsername = (organizationName) => {
    const url = new URL(`${BASE_URL}/practice/getPracticeUsername`);
    const params = { organizationName };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        console.log(data);
        checkUserNameValidity(data.userName.length >= 2 && data.userName <= 20);
        setUserName(data.userName);
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickCell = (col, value) => {
    localStorage.setItem('practice', value);
    const idx = gridData.findIndex(el => el.practiceName === value);
    const practiceNpi = gridData[idx].npi;
    // history.push('/practice');
    localStorage.setItem('lastBookmark', '/admin/practices');
    history.push(`/practice/${practiceNpi}/profile`);
  };

  const handleOnClickResetFilters = () => {
    console.log('Grid reset function not implemented yet');
  };

  const handleOnPopupMenu = (e, idx) => {
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickShowDetailsMenuItem = () => {
    localStorage.setItem('practice', gridData[popupMenuIndex].practiceName);
    history.push('/practice');
  };

  const handleOnClickDeleteMenuItem = () => {
    const id = (gridData[popupMenuIndex]).id;
    toggleGridEllipsisMenu(false);
    deletePracticeProfile(id);
  };

  const handleOnEditPracticeName = (e) => {
    const { value } = e.target;
    setPracticeName(value);
  };

  const handleOnEditDisplayName = (e) => {
    const { value } = e.target;
    setDisplayNamePristineState(false);
    checkDisplayNameValidity(value.length >= 2 && value.length <= 40);
    setDisplayName(value);
  };

  const handleOnEditUserName = (e) => {
    const { value } = e.target;
    setUserNamePristineState(false);
    checkUserNameValidity(value.length >= 2 && value.length <= 20 && value.indexOf(' ') < 0);
    setUserName(value);
  };

  const handleOnEditMailingStreetAddress = (e) => {
    const { value } = e.target;
    setMailingStreetAddress(value);
  };

  const handleOnEditMailingSuite = (e) => {
    const { value } = e.target;
    setMailingSuite(value);
  };

  const handleOnEditMailingCity = (e) => {
    const { value } = e.target;
    setMailingCity(value);
  };

  const handleOnEditMailingState = (e) => {
    const { value } = e.target;
    setMailingState(value);
  };

  const handleOnEditMailingZip = (e) => {
    const { value } = e.target;
    setMailingZip(value);
  };

  const handleOnEditPrimaryStreetAddress = (e) => {
    const { value } = e.target;
    setPrimaryStreetAddress(value);
  };

  const handleOnEditPrimarySuite = (e) => {
    const { value } = e.target;
    setPrimarySuite(value);
  };

  const handleOnEditPrimaryCity = (e) => {
    const { value } = e.target;
    setPrimaryCity(value);
  };

  const handleOnEditPrimaryState = (e) => {
    const { value } = e.target;
    setPrimaryState(value);
  };

  const handleOnEditPrimaryZip = (e) => {
    const { value } = e.target;
    setPrimaryZip(value);
  };

  const handleOnEditEmail = (e) => {
    const { value } = e.target;
    setEmailPristineState(false);
    checkEmailValidity(isEmail(value));
    setEmail(value);
  };

  const handleOnEditPhone = (e) => {
    const { value } = e.target;
    setPhonePristineState(false);
    checkPhoneValidity(isMobilePhone(value, 'en-US'));
    setPhone(value);
  };

  const handleOnEditFax = (e) => {
    const { value } = e.target;
    setFax(value);
  };

  const handleOnEditWebsite = (e) => {
    const { value } = e.target;
    setWebsite(value);
  };

  const handleOnChangeProfileDescription = (e) => {
    const { value } = e.target;
    setProfileDescription(value);
  };

  // Select search items from the last name search menu
  const selectPractice = (data) => {
    if (data.userName) {
      console.log('Practice already exists');
      return;
    }
    console.log(data);
    const { organization_name: organizationName } = data.basic;
    const { number, addresses, taxonomies } = data;
    const formattedOrgName = generateDisplayName(organizationName);
    console.log('formattedOrgName', formattedOrgName);
    setDisplayName(formattedOrgName);
    getUniquePracticeUsername(formattedOrgName);
    setPracticeName(formattedOrgName);
    setNpi(number);

    const taxIdx = taxonomies.findIndex(el => el.primary);
    if (taxIdx > -1) {
      setPracticeTaxonomy(taxonomies[taxIdx].desc);
    }
    if (Array.isArray(addresses) && addresses.length > 0) {
      addresses.forEach((address) => {
        if (address.telephone_number) {
          setPhone(address.telephone_number || '');
        }
        if (address.address_purpose === 'MAILING') {
          setMailingStreetAddress(address.address_1 || '');
          setMailingCity(address.city || '');
          setMailingState(address.state || '');
          setMailingZip(address.postal_code || '');
        }
        if (address.address_purpose === 'LOCATION') {
          setPrimaryStreetAddress(address.address_1 || '');
          setPrimaryCity(address.city || '');
          setPrimaryState(address.state || '');
          setPrimaryZip(address.postal_code || '');
        }
      });
    }
  };

  const addPracticeProfile = () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('npi', npi);
    formData.append('practiceName', practiceName);
    formData.append('displayName', displayName);
    formData.append('userName', userName);
    // Primary address
    formData.append('streetAddress', primaryStreetAddress);
    formData.append('suite', primarySuite);
    formData.append('city', primaryCity);
    formData.append('state', primaryState);
    formData.append('zip', primaryZip);
    // Mailing address
    formData.append('mailingStreetAddress', mailingStreetAddress);
    formData.append('mailingSuite', mailingSuite);
    formData.append('mailingCity', mailingCity);
    formData.append('mailingState', mailingState);
    formData.append('mailingZip', mailingZip);
    // ---
    formData.append('email', email);
    formData.append('phone', phone);
    formData.append('fax', fax);
    formData.append('website', website);
    formData.append('avatarImageUrl', avatarImageUrl);
    formData.append('bannerImageUrl', bannerImageUrl);
    formData.append('profileDescription', profileDescription);
    formData.append('taxonomy', practiceTaxonomy);
    // formData.append('practiceMembers', JSON.stringify(practiceMembers));

    const requestOptions = {
      method: 'PUT',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/practice/addPractice`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          closeAddPracticeDrawer();
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const deletePracticeProfile = (_id) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('_id', _id);

    const requestOptions = {
      method: 'DELETE',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/practice/deletePractice`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadBanner = () => {
    const formData = new FormData();
    const img = bannerFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedBanner = (blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'croppedBanner.png');
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped banner image url', res.fileUrl);
          setBannerImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsBannerUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileSelected = () => {
    if (bannerFileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', bannerFileInputRef.current.files[0]);
      // console.log('typeof img', typeof bannerFileInputRef.current.files[0]);
      setIsBannerUploading(true);
      uploadBanner();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickBannerCrop = () => {
    const imageElement = bannerCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerBannerRef.current.src = url;
      uploadCroppedBanner(blob);
      toggleBannerCropperDrawer(false);
    });
  };

  const uploadAvatar = () => {
    const formData = new FormData();
    const img = avatarFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedAvatar = (blob) => {
    const formData = new FormData();
    formData.append('file', blob);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped avatar image url', res.fileUrl);
          setAvatarImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsAvatarUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileSelected = () => {
    if (avatarFileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', avatarFileInputRef.current.files[0]);
      // console.log('typeof img', typeof avatarFileInputRef.current.files[0]);
      setIsAvatarUploading(true);
      uploadAvatar();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickAvatarCrop = () => {
    const imageElement = avatarCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerAvatarRef.current.src = url;
      uploadCroppedAvatar(blob);
      toggleAvatarCropperDrawer(false);
    });
  };

  const resetState = (closeDrawer = true) => {
    toggleBannerCropperDrawer(false);
    toggleAvatarCropperDrawer(false);
    if (closeDrawer) {
      toggleAddPracticeDrawer(false);
    }
    setDisplayName('');
    checkDisplayNameValidity(false);
    setDisplayNamePristineState(true);
    setUserName('');
    checkUserNameValidity(false);
    setUserNamePristineState(true);
    setUserNameDuplicateStatus(true);
    setMailingStreetAddress('');
    setMailingSuite('');
    setMailingCity('');
    setMailingState('');
    setMailingZip('');
    setPrimaryStreetAddress('');
    setPrimarySuite('');
    setPrimaryCity('');
    setPrimaryState('');
    setPrimaryZip('');
    setPhone('');
    checkPhoneValidity(false);
    setPhonePristineState(true);
    setFax('');
    setProfileDescription('');
    setBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
    setAvatarImageUrl('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
    setShowBannerCropper(false);
    setIsBannerUploading(false);
    setShowAvatarCropper(false);
    setIsAvatarUploading(false);
    setEmail('');
    checkEmailValidity(false);
    setEmailPristineState(true);
    setEmailDuplicateStatus(true);
    setNpi('');
    setWebsite('');
    setCanAddPractice(false);
    setPracticeName('');
  };

  const closeAddPracticeDrawer = () => {
    toggleAddPracticeDrawer(false);
    resetState();
  };

  const resetPracticeSearchDrawerState = () => {
    setSearchOrgName('');
    setSearchTaxonomy('');
    setSearchNpi('');
    setSearchCity('');
    setSearchZip('');
    setShowStateMenu(false);
    setSearchState('');
    setSearchingPractices(false);
    setPracticeSearchResults([]);
    setShowZeroResultMessage(false);
  };

  const handleOnClickSavePracticeProfile = () => {
    if (canAddPractice) {
      addPracticeProfile();
    }
  };

  const handleOnChangeSearchOrgName = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchOrgName(value);
  };

  const handleOnChangeSearchTaxonomy = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchTaxonomy(value);
  };

  const handleOnChangeSearchNpi = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchNpi(value);
  };

  const handleOnChangeSearchCity = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchCity(value);
  };

  const handleOnChangeSearchZip = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchZip(value);
  };

  const handleOnClickResetPracticeSearch = () => {
    setSearchOrgName('');
    setSearchTaxonomy('');
    setSearchNpi('');
    setSearchCity('');
    setSearchState('');
    setSearchZip('');
    setPracticeSearchResults([]);
  };

  const handleOnClickSearchPractices = () => {
    if (searchingPractices) {
      controller.abort();
      resetPracticeSearchDrawerState();
      return;
    }

    controller = new AbortController();
    const url = new URL(`${BASE_URL}/practice/searchPracticesNppes`);
    const params = {
      orgName: searchOrgName,
      taxonomy: searchTaxonomy,
      npi: searchNpi,
      state: searchState === 'ALL' ? '' : searchState,
      city: searchCity,
      zip: searchZip
    };
    url.search = new URLSearchParams(params).toString();

    setSearchingPractices(true);
    fetch(url, { signal: controller.signal })
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        console.log(data.practices);
        setSearchingPractices(false);
        if (data.practices.some(p => p.enumeration_type === 'NPI-1')) {
          props.addAppAlert({
            headerText: 'Warning',
            headerIcon: 'fa fa-bell',
            bodyText: 'Only type 2 NPI search allowed!',
            showAlert: true,
            autoDismiss: true,
            autoDismissTime: 5000
          });
          setSearchOrgName('');
          setSearchTaxonomy('');
          setSearchNpi('');
          setSearchNpi('');
          setSearchCity('');
          setSearchState('');
          setSearchZip('');
        } else {
          data.practices.sort((a, b) => b.hasMedprosProfile - a.hasMedprosProfile);
          setPracticeSearchResults(data.practices);
          if (data.practices && Array.isArray(data.practices) && data.practices.length === 0) {
            setShowZeroResultMessage(true);
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickTogglePracticeSearchDrawer = () => {
    resetPracticeSearchDrawerState();
    setShowSearchPracticeDrawer(true);
  };

  const handleOnCloseSearchPracticeDrawer = () => {
    setShowSearchPracticeDrawer(false);
  };

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Practice search drawer */}
      {showSearchPracticeDrawer &&
        <Drawer
          placement="right"
          visible={showSearchPracticeDrawer}
          toggleDrawer={handleOnCloseSearchPracticeDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          {/* Drawer Container */}
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-building' />
                <div className='noselect'>Search Practice</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={handleOnCloseSearchPracticeDrawer}
              />
            </div>
            {/* Middle Area */}
            <div className={css(styles.drawerContentArea)}>
              <div className='pl-2 pr-2'>
                <div className='mt-3'>
                  {/* Organization Name */}
                  <div>
                    {/* Org Name */}
                    <TextInputWithLabel
                      value={searchOrgName}
                      titleText="Org Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={334}
                      placeholder={!showNpiErrMessage && searchNpi.length === 10 ? null : 'Required'}
                      onChange={handleOnChangeSearchOrgName}
                    />
                  </div>
                  {/* Taxonomy and NPI */}
                  <div className='fx'>
                    {/* Taxonomy */}
                    <TextInputWithLabel
                      value={searchTaxonomy}
                      titleText="Taxonomy"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={163}
                      onChange={handleOnChangeSearchTaxonomy}
                    />
                    {/* NPI */}
                    <TextInputWithLabel
                      value={searchNpi}
                      className='ml-2'
                      titleText="NPI"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={163}
                      onChange={handleOnChangeSearchNpi}
                    />
                  </div>
                  {showNpiErrMessage &&
                    <div
                      className={css(styles.npiErrMessageContainer)}
                    >
                      <div className={css(styles.npiErrMessage)}>
                        {npiErrMsg}
                      </div>
                    </div>
                  }
                  {/* City, State & Zip */}
                  <div className='fx' style={{ alignItems: 'flex-end' }}>
                    <TextInputWithLabel
                      value={searchCity}
                      titleText="City"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={192}
                      onChange={handleOnChangeSearchCity}
                    />
                    <div
                      ref={stateRef}
                      style={{ position: 'relative' }}
                    >
                      <div
                        className='ml-2'
                        onClick={() => setShowStateMenu(true)}
                        style={{
                          height: '24px',
                          width: '43px',
                          // background: blue[900],
                          background: '#103A60',
                          color: blue[200],
                          fontSize: '12px',
                          padding: '2px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          bottom: '-2px'
                        }}
                      >
                        {`${searchState.length === 0 ? 'ALL' : searchState} `}
                        <i
                          style={{ fontSize: '16px' }}
                          className={`ml-1 fa ${showStateMenu ? 'fa-caret-up' : 'fa-caret-down'}`}
                        />
                      </div>
                      <PopupMenu
                        top={28}
                        left={11}
                        zIndex='1550'
                        position="absolute"
                        visible={showStateMenu}
                        parent={stateRef}
                        onOutsideClick={() => setShowStateMenu(false)}
                      >
                        <div
                          style={{
                            background: '#0a3250',
                            width: '128px',
                            height: '200px',
                            overflowY: 'auto'
                          }}
                        >
                          {USA_STATES.map(el => (
                            <div
                              key={el.code}
                              className='m-item'
                              onClick={() => {
                                setShowZeroResultMessage(false);
                                setSearchState(el.code);
                                setShowStateMenu(false);
                              }}
                              style={{
                                height: '32px',
                                borderBottom: '1px solid #001824',
                                cursor: 'pointer',
                                fontSize: '12px',
                                background: gray[700]
                              }}
                            >
                              {el.name}
                            </div>
                          ))}
                        </div>
                      </PopupMenu>
                    </div>
                    <TextInputWithLabel
                      value={searchZip}
                      className='ml-2'
                      titleText="Zip"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={80}
                      onChange={handleOnChangeSearchZip}
                    />
                  </div>
                  {/* Search & Reset Buttons */}
                  <div className='mt-4 fx fx__align-right'>
                    {/* Reset Button */}
                    <Button
                      btnType='primary'
                      isOutlineBtn
                      isDisabled={!canSearchPractices}
                      onClick={handleOnClickResetPracticeSearch}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        width: '100px',
                        height: '32px'
                      }}
                    >
                      Reset
                    </Button>
                    {/* Search Button */}
                    <Button
                      className='ml-2'
                      isDisabled={!canSearchPractices}
                      btnType='primary'
                      showLoader={searchingPractices}
                      onClick={handleOnClickSearchPractices}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '240px',
                        height: '32px'
                      }}
                    >
                      {searchingPractices ? 'Searching... Click to Cancel' : 'Search'}
                    </Button>
                  </div>
                  {showZeroResultMessage &&
                    <div className='p-2' style={{ color: salmon[500], fontSize: '14px', fontWeight: '700' }}>
                      No Practices Found
                    </div>
                  }
                </div>
                {/* Practices Cards */}
                <div
                  className='mt-2 pl-2 pr-2'
                  style={{
                    height: 'calc(100vh - 250px)',
                    overflowY: 'auto'
                  }}
                >
                  {practiceSearchResults.map(el => {
                    const { organization_name: orgName = '' } = el.basic;
                    const taxIdx = el.taxonomies.findIndex(el => el.primary);
                    const addIdx = el.addresses.findIndex(el => el.address_purpose === 'LOCATION');

                    return (
                      <div
                        key={`${el.number}__provider`}
                        className='mt-2 p-2'
                        style={{
                          background: blue[700],
                          borderRadius: '4px',
                          borderTop: `1px solid ${blue[600]}`,
                          fontSize: '14px',
                          fontWeight: '700',
                          minHeight: '49px'
                        }}
                      >
                        <div>
                          {/* Practice name & NPI */}
                          <div style={{ color: blue[400] }}>
                            <a
                              target='_blank'
                              rel="noreferrer"
                              href={`https://npiregistry.cms.hhs.gov/provider-view/${el.number}`}
                              className='line-clamp'
                              style={{ flexGrow: '1', color: blue[400] }}
                            >
                              {orgName.length > 0 ? `${titleCase(orgName)}` : ''}
                              {orgName.length > 0 && <br></br>}
                              {`NPI ${`${el.number}`}`}
                            </a>
                          </div>
                          {/* Practice taxonomy & primary address */}
                          <div
                            style={{ color: blue[200] }}
                          >
                            <div>
                              {el.taxonomies[taxIdx].desc}
                            </div>
                            <div className='font-14'>
                              {/* {el.addresses[addIdx].address_1} <br></br>
                              {el.addresses[addIdx].address_2 ? `${el.addresses[addIdx].address_2}\n` : ''} */}
                              {titleCase(el.addresses[addIdx].city)}
                              {el.addresses[addIdx].state.length > 0 ? `, ${el.addresses[addIdx].state.toUpperCase()}` : ''}
                            </div>
                          </div>
                        </div>
                        <div>
                          <Button
                            isDisabled={el.hasMedprosProfile}
                            style={{
                              maxWidth: '309px',
                              minWidth: '105px',
                              maxHeight: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '4px',
                              fontSize: '14px'
                            }}
                            onClick={() => {
                              if (!el.hasMedprosProfile) {
                                setShowSearchPracticeDrawer(false);
                                toggleAddPracticeDrawer(true);
                                selectPractice(el);
                              }
                            }}
                          >
                            {el.hasMedprosProfile ? 'Duplicate' : 'Create Profile'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      }
      {/* Add practice drawer */}
      {isAddPracticeDrawerVisible &&
        <Drawer
          placement="right"
          visible={isAddPracticeDrawerVisible}
          toggleDrawer={closeAddPracticeDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          {/* Drawer Container */}
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-building' />
                <div className='noselect'>Create Practice Profile</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={closeAddPracticeDrawer}
              />
            </div>
            {/* Practice Details */}
            <div className={css(styles.drawerContentArea)}>
              <div className='p-2'>
                {/* Banner */}
                <div className={css(styles.drawerBanner)}>
                  <img
                    ref={drawerBannerRef}
                    src={bannerImageUrl}
                    alt="cropped-banner"
                    className={css(styles.drawerBannerImg)}
                  />
                  {/* Camera icon */}
                  <div
                    onClick={() => { toggleBannerCropperDrawer(true); }}
                    className={css(styles.drawerBannerCameraIcon)}
                  >
                    <i className="fa fa-camera" />
                  </div>
                </div>
                {/* Middle area */}
                <div>
                  {/* NPI  */}
                  <div className={css(styles.practiceNameHeader)}>
                    <a
                      target='_blank'
                      rel="noreferrer"
                      href={`https://npiregistry.cms.hhs.gov/provider-view/${npi}`}
                      className='line-clamp'
                      style={{ flexGrow: '1', color: blue[400] }}
                    >
                      <div>{practiceName}</div>
                      <div>NPI: {npi}</div>
                    </a>
                  </div>
                  {/* Practice Name */}
                  <div style={{ display: 'none' }}>
                    <TextInputWithLabel
                      value={practiceName}
                      titleText='Practice Name*'
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={342}
                      onChange={handleOnEditPracticeName}
                    />
                  </div>
                  {/* User card */}
                  <div className="fx mt-4 ml-2">
                    {/* Avatar */}
                    <div className={css(styles.drawerUserCardAvatar)}>
                      <img
                        src={avatarImageUrl}
                        ref={drawerAvatarRef}
                        alt="avatar"
                        className={css(styles.drawerUserCardAvatarImg)}
                        onClick={() => toggleAvatarCropperDrawer(true)}
                      />
                      <div
                        onClick={() => { toggleAvatarCropperDrawer(true); }}
                        className={css(styles.drawerAvatarCamera)}
                      >
                        <i className="fa fa-camera" />
                      </div>
                    </div>
                    {/* User card displayName & userName */}
                    <div className={`ml-2 ${css(styles.drawerUserCardText)}`}>
                      {/* Display Name */}
                      <div style={{ marginTop: '-5px' }}>
                        <TextInputWithLabel
                          value={displayName}
                          titleText="Display Name (2 to 40 characters)*"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={248}
                          onChange={handleOnEditDisplayName}
                        />
                        {/* Error message */}
                        {!isDisplayNameValid && !isDisplayNamePristine
                          ? <div className={css(styles.errMsg)}>
                            Invalid Display Name
                          </div>
                          : null
                        }
                      </div>
                      {/* UserName */}
                      <div
                        style={{
                          marginTop: (!isDisplayNameValid && !isDisplayNamePristine) ? '0px' : '4px'
                        }}
                      >
                        <TextInputWithLabel
                          value={userName}
                          titleText="Username (2 to 20 characters, no spaces)*"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor={blue[500]}
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={248}
                          onChange={handleOnEditUserName}
                        />
                        {/* Error message */}
                        {!isUserNameValid && !isUserNamePristine
                          ? <div className={css(styles.errMsg)}>
                            Invalid User Name
                          </div>
                          : null
                        }
                        {isUserNameValid && !isUserNamePristine && isUserNameDuplicate
                          ? <div className={css(styles.errMsg)}>
                            User Name Taken
                          </div>
                          : null
                        }
                      </div>
                    </div>
                  </div>
                  {/* Primary Address */}
                  <div className="mt-4">
                    {/* Heading */}
                    <div className="fx fx__v-center">
                      <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                      <div className='ml-1'>Primary Address</div>
                    </div>
                    {/* Form */}
                    <div>
                      {/* Address */}
                      <div className="fx">
                        <div>
                          <TextInputWithLabel
                            value={primaryStreetAddress}
                            titleText="Street"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={284}
                            onChange={handleOnEditPrimaryStreetAddress}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={primarySuite}
                            titleText="Suite"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditPrimarySuite}
                          />
                        </div>
                      </div>
                      {/* City, State and Zip */}
                      <div className="fx mt-2">
                        <div>
                          <TextInputWithLabel
                            value={primaryCity}
                            titleText="City"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={230}
                            onChange={handleOnEditPrimaryCity}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={primaryState}
                            titleText="State"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditPrimaryState}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={primaryZip}
                            titleText="Zip"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditPrimaryZip}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Mailing Address */}
                  <div className="mt-4">
                    {/* Heading */}
                    <div className="fx fx__v-center">
                      <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                      <div className='ml-1'>Mailing Address</div>
                    </div>
                    {/* Form */}
                    <div>
                      {/* Address */}
                      <div className="fx">
                        <div>
                          <TextInputWithLabel
                            value={mailingStreetAddress}
                            titleText="Street"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={284}
                            onChange={handleOnEditMailingStreetAddress}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={mailingSuite}
                            titleText="Suite"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditMailingSuite}
                          />
                        </div>
                      </div>
                      {/* City, State and Zip */}
                      <div className="fx mt-2">
                        <div>
                          <TextInputWithLabel
                            value={mailingCity}
                            titleText="City"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={230}
                            onChange={handleOnEditMailingCity}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={mailingState}
                            titleText="State"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditMailingState}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={mailingZip}
                            titleText="Zip"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditMailingZip}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Email, Phone, Fax, Website */}
                  <div>
                    {/* Heading */}
                    <div className='mt-4'>Contact</div>
                    {/* Email */}
                    <div style={{ marginTop: '-12px' }}>
                      <TextInputWithLabel
                        value={email}
                        titleText="Email*"
                        titleFontSize={12}
                        isCompactLayout={false}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={337}
                        showPreIcon
                        preIcon='fa fa-envelope'
                        onChange={handleOnEditEmail}
                      />
                      {!isEmailPristine && !isEmailValid
                        ? <div className={css(styles.errMsg)}>Invalid Email</div>
                        : null
                      }
                      {isEmailValid && !isEmailPristine && isEmailDuplicate
                        ? <div className={css(styles.errMsg)}>Email already in use</div>
                        : null
                      }
                    </div>
                    {/* Phone */}
                    <div className="mt-2">
                      <TextInputWithLabel
                        value={phone}
                        titleText="Phone #"
                        titleFontSize={12}
                        isCompactLayout={false}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={337}
                        showPreIcon
                        preIcon='fa fa-phone'
                        onChange={handleOnEditPhone}
                      />
                      {!isPhonePristine && !isPhoneValid
                        ? <div className={css(styles.errMsg)}>Invalid Phone #</div>
                        : null
                      }
                    </div>
                    {/* Fax */}
                    <div className="mt-2">
                      <TextInputWithLabel
                        value={fax}
                        titleText="Fax #"
                        titleFontSize={12}
                        isCompactLayout={false}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={337}
                        showPreIcon
                        preIcon='fa fa-fax'
                        onChange={handleOnEditFax}
                      />
                    </div>
                    {/* Website */}
                    <div className="mt-2">
                      <TextInputWithLabel
                        value={website}
                        titleText="Website"
                        titleFontSize={12}
                        isCompactLayout={false}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={337}
                        showPreIcon
                        preIcon='fa fa-globe'
                        onChange={handleOnEditWebsite}
                      />
                    </div>
                  </div>
                  {/* Profile descriptiom */}
                  <div className="mt-4">
                    <div>Profile Description</div>
                    <div className={css(styles.profileTextAreaContainer)}>
                      <textarea
                        className={css(styles.profileTextArea)}
                        placeholder="Write a description"
                        value={profileDescription}
                        onChange={handleOnChangeProfileDescription}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
              {/* Required fields msg */}
              <div className={css(styles.criticalSmallText)} >
                *Required fields
              </div>
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSavePracticeProfile}
                isDisabled={!canAddPractice}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Create Practice Profile
              </Button>
            </div>
          </div>
        </Drawer>
      }
      {/* Profile banner image cropper */}
      <Drawer
        placement="right"
        visible={isBannerCropperDrawerOpen}
        toggleDrawer={() => toggleBannerCropperDrawer(false)}
        zIndex="2050"
        width={window.innerWidth <= 700 ? window.innerWidth : 358}
      >
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div>Upload Profile Banner</div>
            <i
              className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
              onClick={() => { toggleBannerCropperDrawer(false); }}
            />
          </div>
          {/* Content Area */}
          <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
            {showBannerCropper
              ? <div>
                <Cropper
                  src={bannerImageUrl}
                  style={{ height: 400, width: '100%' }}
                  checkCrossOrigin
                  checkOrientation={false}
                  aspectRatio={6}
                  background={false}
                  viewMode={1}
                  autoCropArea={1}
                  guides={false}
                  ref={bannerCropperRef}
                />
              </div>
              : null
            }
            {!showBannerCropper
              ? <div>
                <FileUploader
                  parentRef={bannerFileInputRef}
                  onDrop={handleOnBannerFileDrop}
                  onChange={handleOnBannerFileSelected}
                />
              </div>
              : null
            }
            {isBannerUploading
              ? <div className={css(styles.imageUploadLoader)}>
                <Loader iconType="cog" iconSize="5x" text="Uploading Banner Image" />
              </div>
              : null
            }
          </div>
          {/* Drawer footer area */}
          <div
            className={`fx pr-2 ${css(styles.drawerFooter)}`}
            style={{ position: 'absolute', bottom: '0' }}
          >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickBannerCrop}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Crop Banner Image
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Profile avatar image cropper */}
      <Drawer
        placement="right"
        visible={isAvatarCropperDrawerOpen}
        toggleDrawer={() => toggleAvatarCropperDrawer(false)}
        zIndex="2050"
        width={window.innerWidth <= 700 ? window.innerWidth : 358}
      >
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div>Upload Profile Avatar</div>
            <i
              className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
              onClick={() => { toggleAvatarCropperDrawer(false); }}
            />
          </div>
          {/* Content Area */}
          <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
            {showAvatarCropper
              ? <div>
                <Cropper
                  src={avatarImageUrl}
                  style={{ height: 400, width: '100%' }}
                  checkCrossOrigin
                  checkOrientation={false}
                  aspectRatio={1}
                  background={false}
                  viewMode={1}
                  autoCropArea={1}
                  guides={false}
                  ref={avatarCropperRef}
                />
              </div>
              : null
            }
            {!showAvatarCropper
              ? <div>
                <FileUploader
                  parentRef={avatarFileInputRef}
                  onDrop={handleOnAvatarFileDrop}
                  onChange={handleOnAvatarFileSelected}
                />
              </div>
              : null
            }
            {isAvatarUploading
              ? <div className={css(styles.imageUploadLoader)}>
                <Loader iconType="cog" iconSize="5x" text="Uploading Avatar Image" />
              </div>
              : null
            }
          </div>
          {/* Drawer footer area */}
          <div
            className={`fx pr-2 ${css(styles.drawerFooter)}`}
            style={{ position: 'absolute', bottom: '0' }}
          >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickAvatarCrop}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Crop Avatar Image
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Grid toolbar and users data grid */}
      <div className={css(styles.gridContainer)}>
        {/* Grid toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Grid toolbar - right aligned buttons */}
          <div className='fx'>
            {/* Add practice button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickTogglePracticeSearchDrawer}
            >
              <i className="fa fa-lg fa-plus-circle" />
            </div>
            {/* Download */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-cloud-download" />
            </div>
            {/* Trash bin */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-trash" />
            </div>
            {/* Checkbox */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-check-square" />
            </div>
            {/* Reset button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickResetFilters}
            >
              <i className="fa fa-lg fa-refresh" />
            </div>
            {/* Vertical ellipsis */}
            <div
              className={css(styles.gridToolbarIcon)}
              style={{ fontSize: '17px' }}
            >
              <span
                className='fa-stack'
                style={{ fontSize: '14px' }}
              >
                <i className='fa fa-circle fa-stack-2x' />
                <i
                  className='fa fa-ellipsis-v fa-stack-1x'
                  style={{ color: gray[700] }}
                />
                {/* <i class="fa fa-flag fa-stack-1x fa-inverse"></i> */}
              </span>
            </div>
          </div>
        </div>
        {/* Grid ellipsis menu */}
        <div
          ref={gridEllipsisMenu}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenu}
            onOutsideClick={() => { toggleGridEllipsisMenu(false); }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Show details menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickShowDetailsMenuItem}
              >
                Show Details
              </div>
              {/* Delete record menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickDeleteMenuItem}
              >
                Delete Record
              </div>
            </div>
          </PopupMenu>
        </div>
        {/* Users' Data Grid */}
        <DataGrid
          loader={showLoader}
          icons={icons}
          headerDefs={headerDefs}
          rowData={gridData}
          filters={filters}
          gridWidth={gridWidth}
          gridHeight={gridHeight}
          gridHeaderHeight={48}
          gridFooterHeight={0}
          gridContainerHeight='730px'
          rowHeight={64}
          rowsPerPage={DOCS_PER_PAGE}
          defaultCellStyle={defaultCellStyle}
          showFooter={false}
          showPager
          showEllipsisCol
          enableHover
          totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
          currentPage={currentPage}
          isMobileLayout={isMobileLayout}
          onClickCell={handleOnClickCell}
          onApplyFilter={() => { console.warn('Handler not set for onApplyFilter prop.'); }}
          onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
          onLoadPage={() => { console.warn('Handler not set for onLoadPage prop.'); }}
          onColFilterToggle={() => { console.warn('Handler not set for onColFilterToggle prop.'); }}
          onFilterTypeUpdate={() => { console.warn('Handler not set for onFilterTypeUpdate prop.'); }}
          onColPinChange={() => { console.warn('Handler not set for onColPinChange prop.'); }}
          onResize={() => { console.warn('Handler not set for onResize prop.'); }}
          onPopupMenu={handleOnPopupMenu}
          onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
          onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
          onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
          onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
          onSort={() => { console.warn('Handler not set for onSort prop.'); }}
          onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
        />
      </div>
    </div>
  );
};

function mapStateToProps({ user }) {
  return user;
}
export default connect(
  mapStateToProps,
  { addAppAlert })(PracticesDataGrid);
